import { HttpClient, HttpResponse } from "@angular/common/http";
import { computed, inject, Injectable } from "@angular/core";
import { AppEnvironment } from "../../app.config";
import { firstValueFrom } from "rxjs";
import { GetMedicalReviewItemsResponse, ReviewQueryParams } from "./requests/getMedicalReviewItems";
import { GetMedicalReviewHistoryResponse, ReviewHistoryQueryParams } from "./requests/getMedicalReviewHistory";
import { AuditStatus } from "./requests/submitMedicalReview";
import { ViewMedicalRecordResponse } from "./requests/getMedicalRecord";
import { UpdateCertificateDetailsForm } from "./requests/updateCertificateDetails";
import { ApiResponse } from "./models/api-response.model";
import { GetActiveReviewResponse } from "./models/active-review.model";

@Injectable()
export class ConfigurationService {

    private environment: AppEnvironment | null;
    private currentReleaseVersion: string;

    constructor() {
        this.environment = null;
        this.currentReleaseVersion = '';
    }

    setEnvironment(appEnvironment: AppEnvironment) {
        this.environment = appEnvironment;
    }

    getEnvironment(): AppEnvironment | null {
        return this.environment;
    }

    setReleaseVersion(releaseVersion?: string) {
        this.currentReleaseVersion = releaseVersion ?? '';
    }

    releaseVersion(): string {
        return this.currentReleaseVersion;
    }

    apiHost = computed(() => this.environment?.api ?? '');
}

function toHttpParams(params: object): Record<string, string> {
    return Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== '').map(([k, v]) => [k, String(v)])
    );
}

@Injectable()
export class ApiService {
    private http = inject(HttpClient);
    private config = inject(ConfigurationService);

    getReviewItems(params?: ReviewQueryParams): Promise<GetMedicalReviewItemsResponse> {
        return firstValueFrom(
            this.http.get<GetMedicalReviewItemsResponse>(`${this.config.apiHost()}/api/reviews`, {
                params: toHttpParams(params ?? {})
            })
        );
    }

    getReviewHistory(params?: ReviewHistoryQueryParams): Promise<GetMedicalReviewHistoryResponse> {
        return firstValueFrom(
            this.http.get<GetMedicalReviewHistoryResponse>(`${this.config.apiHost()}/api/reviews/history`, {
                params: toHttpParams(params ?? {})
            })
        );
    }

    submitReview(employeeId: number, auditType: AuditStatus, notes?: string): Promise<ApiResponse> {
        return firstValueFrom(
            this.http.post<ApiResponse>(`${this.config.apiHost()}/api/medicals/${employeeId.toString()}/review`, {
                employeeId: employeeId,
                status: auditType,
                notes: notes
            })
        );
    }

    viewMedicalRecord(employeeId: number): Promise<ViewMedicalRecordResponse> {
        return firstValueFrom(
            this.http.get<ViewMedicalRecordResponse>(`${this.config.apiHost()}/api/medicals/${employeeId.toString()}`)
        );
    }

    updateMedicalCertificateDetails(employeeId: number, form: UpdateCertificateDetailsForm) {
        return firstValueFrom(
            this.http.patch<ApiResponse>(`${this.config.apiHost()}/api/medicals/${employeeId.toString()}`, form)
        );
    }

    uploadMedicalCertificate(employeeId: number, file: File, doctorsName: string, dateOfExam: Date, doctorsNotes: string | null, dateOfLimitation: Date | null) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('details', JSON.stringify({
            doctor: doctorsName,
            notes: doctorsNotes,
            dateOfExam: dateOfExam,
            dateOfLimitation: dateOfLimitation
        }));

        return firstValueFrom(
            this.http.post<Pick<ApiResponse, 'errors'> | null>(`${this.config.apiHost()}/api/medicals/${employeeId.toString()}`, formData)
        );
    }

    revokeMedicalCertificate(employeeId: number) {
        return firstValueFrom(
            this.http.delete<ApiResponse>(`${this.config.apiHost()}/api/medicals/${employeeId.toString()}`)
        );
    }

    submitAudit(employeeId: number, auditType: AuditStatus, failureReason?: string) {
        return this.http.post(`${this.config.apiHost()}/api/reviews`, {
            employeeId: employeeId,
            status: auditType,
            notes: failureReason ?? null
        });
    }

    getActiveReview(medicalCertificateId: string): Promise<GetActiveReviewResponse> {
        return firstValueFrom(
            this.http.get<GetActiveReviewResponse>(`${this.config.apiHost()}/api/reviews/${medicalCertificateId}/active`)
        );
    }

    exportReviewHistory(): Promise<HttpResponse<Blob>> {
        return firstValueFrom(
            this.http.get(`${this.config.apiHost()}/api/reviews/history/export`, {
                responseType: 'blob',
                observe: 'response'
            })
        );
    }
}
